import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { axiosApi } from "./axios";
import { CreateNoteDto, ExpeditionNotesDto } from "@/types/api/api-types";

//#region Get

const getNotesBo = async (id: string) => {
  return axiosApi.get<[ExpeditionNotesDto]>(`v1/expedition/bo/notes/${id}`).then((res) => res.data);
};

export const useGetNotesBo = (id: string) => {
  return useQuery({
    queryKey: ["expedition", "bo", "notes", id],
    queryFn: () => getNotesBo(id),
    enabled: !!id,
  });
};

//#endregion

//#region Post

const createNoteBo = async ({
  id,
  payload,
  type,
  subType,
}: {
  id: string;
  payload: string;
  type?: CreateNoteDto["type"];
  subType?: CreateNoteDto["subType"];
}) => {
  return axiosApi.post<{ id: string }>(`v1/expedition/bo/notes/${id}`, {
    note: payload,
    ...(type ? { type } : {}),
    ...(subType ? { subType } : {}),
  });
};

export const useCreateNoteBo = (successMessage?: string) => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createNoteBo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["expedition", "bo", "notes"],
      });
      queryClient.invalidateQueries({
        queryKey: ["expedition", "all", "bo"],
      });
      toast.success(successMessage || t("noteToasts.createNoteBoSuccess"));
    },
    onError: () => {
      toast.error(t("noteToasts.createNoteBoError"));
    },
  });
};

//#endregion

//#region Delete

const deleteNoteBo = async (noteId: string) => {
  return axiosApi.delete(`v1/expedition/bo/notes/${noteId}`);
};

export const useDeleteNoteBo = () => {
  const { t } = useTranslation("boExpeditions");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNoteBo,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["expedition", "bo", "notes"],
      });
      queryClient.invalidateQueries({
        queryKey: ["expedition", "all", "bo"],
      });
    },
    onError: () => {
      toast.error(t("noteToasts.deleteNoteBoError"));
    },
  });
};

//#endregion
